import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFeatures } from "@/lib/features";
import { Flag, Target, CalendarDays, Trophy, Loader2, ArrowRight, Plus } from "lucide-react";
import { toast } from "sonner";

export function CoachingModeCard({ onChange }) {
  const features = useFeatures();
  const [st, setSt] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ race_name: "", race_distance: "10km", race_date: "", target_time: "" });
  const [reco, setReco] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/coaching/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const refresh = async () => { await load(); onChange && onChange(); };

  const submitAddRace = async () => {
    if (!form.race_date || !form.race_distance) return toast.error("Distance et date requises");
    setBusy(true);
    try {
      const r = await api.post("/coaching/add-race", form);
      setReco(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const switchToEvent = async () => {
    setBusy(true);
    try {
      await api.post("/coaching/switch-to-event");
      toast.success("Préparation calée jusqu'au jour J — tes séances réalisées sont conservées.");
      setAdding(false); setReco(null);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const completeRace = async () => {
    setBusy(true);
    try {
      await api.post("/coaching/complete-race");
      toast.success("Bravo ! Place à la récupération — MAI repasse en coaching continu.");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (!st || !st.has_plan) return null;

  // ---- MODE ÉPREUVE ----
  if (st.coaching_mode === "event") {
    return (
      <div className="mb-6" data-testid="coaching-mode-card">
        <div className="rounded-2xl bg-obsidian text-white p-5 sm:p-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><Trophy size={18} className="text-sage" /></span>
              <div>
                <div className="text-[10px] tracking-[0.16em] uppercase text-white/40">Préparation d'épreuve</div>
                <div className="text-base font-medium" data-testid="coaching-objective">{st.race?.race_name || st.objective_label}{st.race?.distance ? ` · ${st.race.distance}` : ""}</div>
              </div>
            </div>
            {!st.race_passed && (
              <div className="text-right">
                <div className="font-display text-3xl font-light tabnum" data-testid="coaching-weeks-left">{st.days_left >= 0 ? `J-${st.days_left}` : "🏁"}</div>
                <div className="text-[10px] tracking-[0.14em] uppercase text-white/40">{st.weeks_left} sem. restantes</div>
              </div>
            )}
          </div>
          {st.phase_label && !st.race_passed && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs" data-testid="coaching-phase">
              <Target size={13} className="text-sage" /> Phase actuelle : {st.phase_label}
            </div>
          )}
          {st.race_passed && (
            <div className="mt-4">
              <p className="text-sm text-white/70">Ta course est passée. On enchaîne sur une phase de récupération puis un nouvel objectif.</p>
              <button data-testid="complete-race-button" onClick={completeRace} disabled={busy}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-obsidian px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Flag size={15} />} Terminer ma course
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- MODE CONTINU ----
  return (
    <div className="mb-6" data-testid="coaching-mode-card">
      <div className="rounded-2xl border border-border bg-white p-5 sm:p-6 shadow-premium">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.16em] uppercase text-obsidian/40">Coaching continu</div>
            <div className="text-base sm:text-lg font-medium mt-0.5" data-testid="coaching-objective">{st.objective_label}</div>
            <div className="mt-1 text-sm text-obsidian/55" data-testid="coaching-phase">
              Cycle {st.cycle_number}{st.phase_label ? ` · ${st.phase_label}` : ""}
            </div>
          </div>
          <span className="w-11 h-11 shrink-0 rounded-full bg-sage/12 flex items-center justify-center"><Target size={20} className="text-sage" /></span>
        </div>

        {!adding && !reco && (
          <button data-testid="add-race-button" onClick={() => setAdding(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-obsidian px-4 py-2 text-sm font-medium hover:bg-obsidian hover:text-white transition-[background-color,color]">
            <Plus size={15} /> Ajouter une course
          </button>
        )}

        {adding && !reco && (
          <div className="mt-4 border-t border-border pt-4 space-y-3" data-testid="add-race-form">
            <div className="flex items-center gap-2 text-sm font-medium"><CalendarDays size={16} className="text-sage" /> Ta course</div>
            <input data-testid="add-race-name" value={form.race_name} onChange={(e) => setForm({ ...form, race_name: e.target.value })}
              placeholder="Nom (optionnel)" className="w-full rounded-xl border border-border px-4 py-2.5 text-base focus:ring-1 focus:ring-obsidian focus:outline-none" />
            <div className="grid grid-cols-2 gap-2.5">
              <select data-testid="add-race-distance" value={form.race_distance} onChange={(e) => setForm({ ...form, race_distance: e.target.value })}
                className="rounded-xl border border-border px-3 py-2.5 text-base bg-white focus:ring-1 focus:ring-obsidian focus:outline-none">
                <option value="5km">5 km</option><option value="10km">10 km</option>
                <option value="semi">Semi</option><option value="marathon">Marathon</option>{features.trail_enabled && <option value="trail">Trail</option>}
              </select>
              <input data-testid="add-race-date" type="date" value={form.race_date} onChange={(e) => setForm({ ...form, race_date: e.target.value })}
                className="rounded-xl border border-border px-3 py-2.5 text-base focus:ring-1 focus:ring-obsidian focus:outline-none" />
            </div>
            <div className="flex gap-2.5">
              <button data-testid="add-race-submit" onClick={submitAddRace} disabled={busy}
                className="rounded-full bg-obsidian text-white px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />} Évaluer
              </button>
              <button onClick={() => setAdding(false)} className="rounded-full border border-border px-4 py-2.5 text-sm">Annuler</button>
            </div>
          </div>
        )}

        {reco && (
          <div className="mt-4 border-t border-border pt-4" data-testid="add-race-reco">
            <p className="text-sm text-obsidian/75 leading-relaxed">{reco.message}</p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {reco.can_switch && (
                <button data-testid="switch-to-event-button" onClick={switchToEvent} disabled={busy}
                  className="rounded-full bg-obsidian text-white px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-60">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Trophy size={15} />} Basculer en préparation
                </button>
              )}
              <button onClick={() => { setReco(null); setAdding(false); }} className="rounded-full border border-border px-4 py-2.5 text-sm">Garder mon coaching continu</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CoachingModeCard;
